import { UseCloudFunction } from './firebase.utils';

/**
 * Answer keys and loot key codes are not stored on gameElements docs (players
 * can read those). Managers load them through this call instead; the server
 * only answers for managers of the game, and only loot keys for distributors.
 *
 * Elements saved before the move may still carry these fields themselves, so
 * the merge below keeps whatever the element already has when the server
 * returns nothing for it.
 */

/**
 * @param {string} gameId the game
 * @param {string[]} elementIds the elements to load
 * @returns {Promise<object>} private data keyed by element id ({} on any failure)
 */
export async function fetchPrivateElementData(gameId, elementIds) {
  const ids = (elementIds || []).filter(Boolean);
  if (!gameId || ids.length === 0) return {};
  try {
    const res = await UseCloudFunction('getPrivateElementData', {
      'gameId': gameId,
      'elementIds': ids,
    });
    return res?.success ? (res.data || {}) : {};
  } catch (err) {
    console.error('Error loading private element data:', err);
    return {};
  }
}

/**
 * @param {object} element an element from gameElements
 * @param {object} privateData that element's entry from fetchPrivateElementData
 * @returns {object} a copy of the element with its private fields filled in
 */
export function mergePrivateElementData(element, privateData) {
  if (!element || !privateData) return element;
  const { checkpoints, ...fields } = privateData;
  const merged = { ...element, ...fields };
  if (Array.isArray(merged.checkpoints) && checkpoints) {
    merged.checkpoints = merged.checkpoints.map(cp => (
      cp?.id && checkpoints[cp.id] ? { ...cp, ...checkpoints[cp.id] } : cp
    ));
  }
  return merged;
}

/**
 * Loads one element's private data and returns the merged element.
 * @param {object} element an element from gameElements
 * @returns {Promise<object>} the element with its private fields filled in
 */
export async function withPrivateElementData(element) {
  if (!element?.gameId || !element?.id) return element;
  const data = await fetchPrivateElementData(element.gameId, [element.id]);
  return mergePrivateElementData(element, data[element.id]);
}
